import React, { memo, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { DraggableModal, Scrollbars } from '../../../../../generic'
import { useBowtieLanguage } from '../../../../../hooks'
import { IReduxActions, IReduxReducers } from '../../../../../store'
import { IBarrier, IConsequence, IEscalationFactor, IThreat } from '../../../../../types'
import { FoundedList, FoundedListContainer, FoundedTitleContainer, StyledHorizontalRow, StyledInput } from './styled'

interface IProps {
    onClose: () => void
    show: boolean
    threats: IThreat[]
    consequences: IConsequence[]
}

type ISearchNode = IThreat | IConsequence | IEscalationFactor | IBarrier

type ISearchResult = {
    escId: string
    title: string[]
}

const modalHeight = 500
const modalWidth = 700

const Content: React.FC<{ foundedValues: ISearchResult[]; onSearchClick: (dataId: string) => void }> = props => (
    <Scrollbars height={modalHeight - (32 + 16 + 16 + 16 + 1)}>
        {props.foundedValues.map((e, i) => {
            const escId = e.escId.split('|')

            return (
                <FoundedListContainer key={i} style={i > 0 ? { marginTop: 5 } : {}}>
                    <Scrollbars height={70}>
                        <FoundedList onClick={() => props.onSearchClick(e.escId)}>
                            {e.title.map((title, idx, arr) => (
                                <FoundedTitleContainer
                                    key={idx}
                                    prevEscId={escId[idx - 1]}
                                    escId={escId[idx]}
                                    idx={idx}
                                    length={arr.length - 1}
                                >
                                    <div>{title}</div>
                                </FoundedTitleContainer>
                            ))}
                        </FoundedList>
                    </Scrollbars>
                </FoundedListContainer>
            )
        })}
    </Scrollbars>
)

const ContentMemo = memo(Content)

export const SearchModal: React.FC<IProps> = ({ onClose, show, threats, consequences }) => {
    const ref = useRef<HTMLInputElement>(null)
    const [foundValues, setFoundValues] = useState<ISearchResult[]>([])
    const [opacity, setOpacity] = useState(1)

    const { searchPosition } = useSelector((store: IReduxReducers) => store.searchBarrier)
    const dispatch = useDispatch()
    const i18n = useBowtieLanguage()

    const onSearchClick = (dataId: string): void => {
        dispatch<IReduxActions['searchBarrier']>({
            type: 'SET_SEARCH_BARRIER_DATA_ID',
            payload: { newSearchDataId: dataId }
        })
        setOpacity(0.25)
    }

    const findNode = (
        name: string,
        prefix: 'thr' | 'cnq' | 'bar' | 'esc',
        currentNode: ISearchNode,
        prevId?: string,
        prevTitle?: string[]
    ): ISearchResult[] => {
        const trueEscId = String(prevId ? `${prevId}|${prefix}-${currentNode.id}` : `${prefix}-${currentNode.id}`)

        let result = []

        const searchText = currentNode.name?.toLowerCase()

        if (searchText?.startsWith(name.toLowerCase()) || searchText?.includes(' ' + name.toLowerCase())) {
            const title = currentNode.name
            const trueName = prevTitle ? [...prevTitle, title] : [title]

            result.push({ escId: trueEscId, title: trueName })
        }

        if ((currentNode as IBarrier).escalations?.length > 0) {
            const title = currentNode.name
            const trueName = prevTitle ? [...prevTitle, title] : [title]
            ;(currentNode as IBarrier).escalations.map(e => {
                const res = findNode(name, 'esc', e, trueEscId, trueName)

                if (res) {
                    result = [...result, ...res]
                }
            })
        }

        if ((currentNode as IEscalationFactor).barriers?.length > 0) {
            const title = currentNode.name
            const trueName = prevTitle ? [...prevTitle, title] : [title]
            ;(currentNode as IEscalationFactor).barriers.map(e => {
                const res = findNode(name, 'bar', e, trueEscId, trueName)

                if (res) {
                    result = [...result, ...res]
                }
            })
        }

        return result
    }

    const onInputUpdate = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (event.target.value === '' || event.target.value === null || event.target.value === undefined) {
            setFoundValues([])

            return
        }

        let values = []

        threats.map(e => {
            const val = findNode(event.target.value, 'thr', e)
            values = [...values, ...val]
        })

        consequences.map(e => {
            const val = findNode(event.target.value, 'cnq', e)
            values = [...values, ...val]
        })

        setFoundValues(values)
    }

    useEffect(() => {
        if (show) {
            setFoundValues([])

            if (ref.current) {
                ref.current.focus()
            }
        }
    }, [show])

    useEffect(() => {
        if (searchPosition && opacity < 1) {
            setTimeout(() => setOpacity(1), 2000)
        }
    }, [searchPosition]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <DraggableModal
            onClose={onClose}
            show={show}
            height={modalHeight}
            width={modalWidth}
            header={i18n('Search')}
            style={{ opacity }}
            closeButton
        >
            <StyledInput type="text" ref={ref} onChange={onInputUpdate} />
            <StyledHorizontalRow />
            <ContentMemo foundedValues={foundValues} onSearchClick={onSearchClick} />
        </DraggableModal>
    )
}
